"use client";

import { useTranslations } from "next-intl";
import type { ButtonProps } from "@/components/frontend/ui/button";
import { PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious, PaginationRoot } from "./pagination";

export type PaginationMeta = {
	current_page: number;
	last_page: number;
	per_page: number;
	total: number;
};

export type PaginationPagePath = {
	basePath: string;
	/** Search string without a leading `?`. The `page` param is set per link. */
	query?: string;
};

type PaginationProps = {
	className?: string;
	data: { meta: PaginationMeta };
	pagePath?: PaginationPagePath;
	onPageChange?: (page: number) => void;
	variant?: ButtonProps["variant"];
};

function hrefForPage(pagePath: PaginationPagePath | undefined, page: number): string | undefined {
	if (!pagePath) return undefined;

	const params = new URLSearchParams(pagePath.query);
	if (page > 1) params.set("page", page.toString());
	else params.delete("page");

	const query = params.toString();
	return query.length > 0 ? `${pagePath.basePath}?${query}` : pagePath.basePath;
}

function getVisiblePageItems(currentPage: number, totalPages: number): Array<number | "ellipsis"> {
	if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1);

	const pages = new Set<number>([1, totalPages, currentPage, currentPage - 1, currentPage + 1]);
	const validPages = Array.from(pages)
		.filter((page) => page >= 1 && page <= totalPages)
		.sort((a, b) => a - b);
	const result: Array<number | "ellipsis"> = [];

	for (const [index, page] of validPages.entries()) {
		const previousPage = validPages[index - 1];
		if (previousPage && page - previousPage > 1) result.push("ellipsis");
		result.push(page);
	}

	return result;
}

export function Pagination({ className, data, pagePath, onPageChange, variant }: PaginationProps) {
	const t = useTranslations("pagination");
	const visiblePageItems = getVisiblePageItems(data.meta.current_page, data.meta.last_page);

	return (
		<PaginationRoot aria-label={t("label")} className={className}>
			<PaginationContent>
				<PaginationItem>
					<PaginationPrevious
						text={t("previous")}
						aria-label={t("previous-page")}
						variant={variant}
						href={onPageChange ? undefined : hrefForPage(pagePath, data.meta.current_page - 1)}
						onClick={onPageChange ? () => onPageChange(data.meta.current_page - 1) : undefined}
						aria-disabled={data.meta.current_page === 1}
					/>
				</PaginationItem>

				{visiblePageItems.map((page, index) =>
					page === "ellipsis" ? (
						<PaginationItem key={`ellipsis-${index}`}>
							<PaginationEllipsis label={t("more-pages")} />
						</PaginationItem>
					) : (
						<PaginationItem key={page}>
							<PaginationLink
								variant={variant}
								href={onPageChange ? undefined : hrefForPage(pagePath, page)}
								onClick={onPageChange ? () => onPageChange(page) : undefined}
								active={page === data.meta.current_page}
							>
								{page}
							</PaginationLink>
						</PaginationItem>
					),
				)}

				<PaginationItem>
					<PaginationNext
						text={t("next")}
						aria-label={t("next-page")}
						variant={variant}
						href={onPageChange ? undefined : hrefForPage(pagePath, data.meta.current_page + 1)}
						onClick={onPageChange ? () => onPageChange(data.meta.current_page + 1) : undefined}
						aria-disabled={data.meta.current_page === data.meta.last_page}
					/>
				</PaginationItem>
			</PaginationContent>
		</PaginationRoot>
	);
}
